/* estilos-1 (Bloque C, seccion 4 del encargo): EL ESTILO DE EXAMEN AL SUBIR.

   ARCHIVO EN ASCII A PROPOSITO (regla de la casa para los `.js` nuevos).

   Tres piezas, todas detras de la perilla (`lib/estilos.js`; con `on` en falso nadie las monta):
   - `PastillaDeEstilo`: "Style: <nombre largo>" con su flecha; 36 de alto y 44 de toque, 14 px. El valor en
     blanco con borde gris (Quizessa) o en naranja con borde naranja (un examen); si no cabe, "...".
   - `EstiloDeExamen` (la hoja): en el telefono desde abajo, con la agarradera de la casa (`useHojaDeLaCasa`,
     `FilaDeAgarradera`): entra en 500 ms y sale en 300, cierra al bajarla o al tocar fuera SIN cambiar nada, hasta el
     88 % de alto, cuerpo con su propio desplazamiento y "Listo" pegado abajo sobre la zona segura. Desde 768, la
     misma pieza como ventana centrada (el molde de `VentanaDelPanel`): 520 de ancho, 80 % de alto, X arriba a la
     derecha, "Listo" abajo a la derecha, Esc y clic fuera cierran sin cambiar. De arriba a abajo: titulo, renglon,
     buscador (48 de alto y letra de 16: sin zoom), la tarjeta de Quizessa (siempre arriba, tambien buscando), el
     rotulo "Or an exam" y las cuatro categorias (una abierta a la vez; con un examen elegido, la suya llega
     abierta). Buscando, las categorias se van y sale la lista plana de lo que coincide.
   - `SelectorDeEstilo`: la pastilla conectada a la sesion; al tocarla abre la hoja y "Listo" guarda.

   Los nombres de los examenes vienen del servidor; los textos, de `dashboard:estilo.*`. */
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { BarChart3, BookOpen, Check, ChevronDown, GraduationCap, Search, Stethoscope, X } from 'lucide-react';
import { useHojaDeLaCasa, FilaDeAgarradera, ENTRA_EL_VELO, ENTRA_LA_CAJA } from '../alumno/arrastreDeHoja';
import { AreaDe44 } from './anatomia';
import { BOTON_VENTANA_PRINCIPAL } from './VentanaDelPanel';
import {
  QUIZESSA, useEstilos, guardarPreferido, nombreLargo, estiloPorClave, examenesPorCategoria, examenesQueCoinciden,
} from '../../lib/estilos';

export const FONDO_DEL_ESTILO = 'fixed inset-0 z-[70] flex items-end md:items-center justify-center bg-black/[0.66]';
export const CAJA_DEL_ESTILO = 'relative w-full md:max-w-[520px] max-md:max-h-[88vh] max-md:supports-[height:100dvh]:max-h-[88dvh] md:max-h-[80vh] flex flex-col bg-[#111111] border border-[#2A2A2A] rounded-t-[24px] md:rounded-[20px] shadow-2xl';
export const CAMPO_DE_BUSQUEDA = 'w-full h-12 rounded-[14px] bg-[#0E0E0E] border border-[#262626] text-[#F4F4F3] text-[16px] pl-11 pr-12 outline-none focus:border-[#F27A2E] placeholder:text-[#6A6A6A]';

const ICONOS = { medicina: Stethoscope, negocios: BarChart3, posgrado: GraduationCap, prepa: BookOpen };

// -- La pastilla ---------------------------------------------------------------------------------

export function PastillaDeEstilo({ t, idioma, catalogo, valor, alTocar, testid = 'estilo-pastilla', botonRef }) {
  const examen = Boolean(valor && valor !== QUIZESSA && estiloPorClave(catalogo, valor));
  const nombre = nombreLargo(catalogo, examen ? valor : QUIZESSA, idioma) || t('dashboard:estilo.quizessa');
  return (
    <button type="button" ref={botonRef} onClick={alTocar} aria-haspopup="dialog"
      className={`relative inline-flex items-center gap-1.5 h-9 max-w-full min-w-0 px-3.5 rounded-full border text-[14px] leading-5 focus:outline-none focus-visible:ring-2 focus-visible:ring-white ${examen ? 'border-[#FF6B00]' : 'border-[#2C2C2C]'}`}
      data-testid={testid} data-examen={examen ? '' : undefined}>
      <AreaDe44 />
      <span className="shrink-0 text-[#9A9A9A]">{t('dashboard:estilo.etiqueta')}</span>
      <span className={`min-w-0 truncate font-semibold ${examen ? 'text-[#FF6B00]' : 'text-[#F4F4F3]'}`} data-testid={`${testid}-valor`}>{nombre}</span>
      <ChevronDown className="w-4 h-4 shrink-0 text-[#9A9A9A]" aria-hidden="true" />
    </button>
  );
}

// -- Las piezas de la hoja -------------------------------------------------------------------------

function Circulo({ marcado }) {
  return marcado ? (
    <span className="w-6 h-6 shrink-0 rounded-full bg-[#FF6B00] flex items-center justify-center" aria-hidden="true">
      <Check className="w-4 h-4 text-white" strokeWidth={3} />
    </span>
  ) : (
    <span className="w-6 h-6 shrink-0 rounded-full border-2 border-[#4A4A4A]" aria-hidden="true" />
  );
}

function FilaDeExamen({ t, idioma, catalogo, est, marcado, alElegir, primera }) {
  return (
    <button type="button" role="radio" aria-checked={marcado} onClick={() => alElegir(est.clave)}
      className={`w-full min-h-14 flex items-center gap-3 px-4 py-2 text-left focus:outline-none focus-visible:bg-[#1B1B1B] ${primera ? '' : 'border-t border-[#222222]'}`}
      data-testid={`estilo-fila-${est.clave}`}>
      <span className="flex-1 min-w-0">
        <span className="block text-[15px] leading-5 font-semibold text-[#F4F4F3] truncate">{nombreLargo(catalogo, est.clave, idioma)}</span>
        <span className="text-[12px] leading-4 text-[#9A9A9A] line-clamp-2 break-words">{t(`dashboard:estilo.renglones.${est.clave}`)}</span>
      </span>
      <Circulo marcado={marcado} />
    </button>
  );
}

function TarjetaDeCategoria({ t, idioma, catalogo, cat, abierta, alAlternar, elegido, alElegir }) {
  const Icono = ICONOS[cat.clave] || BookOpen;
  return (
    <div className="rounded-[18px] border border-[#222222] bg-[#141414] overflow-hidden" data-testid={`estilo-categoria-${cat.clave}`}>
      <button type="button" onClick={() => alAlternar(cat.clave)} aria-expanded={abierta}
        className="w-full h-14 flex items-center gap-3 px-4 text-left focus:outline-none focus-visible:bg-[#1B1B1B]"
        data-testid={`estilo-categoria-${cat.clave}-boton`}>
        <Icono className="w-5 h-5 shrink-0 text-[#FF6B00]" aria-hidden="true" />
        <span className="flex-1 min-w-0">
          <span className="block text-[15px] leading-5 font-semibold text-[#F4F4F3] truncate">{t(`dashboard:estilo.categorias.${cat.clave}`)}</span>
          <span className="block text-[12px] leading-4 text-[#9A9A9A] truncate">{t('dashboard:estilo.conteo', { count: cat.examenes.length })}</span>
        </span>
        <ChevronDown className={`w-4 h-4 shrink-0 text-[#9A9A9A] transition-transform duration-200 motion-reduce:transition-none ${abierta ? 'rotate-180' : ''}`} aria-hidden="true" />
      </button>
      <div className={`grid transition-[grid-template-rows] duration-200 ease-out motion-reduce:transition-none ${abierta ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}
        data-testid={`estilo-categoria-${cat.clave}-cuerpo`} data-abierta={abierta ? '' : undefined}>
        <div className="min-h-0 overflow-hidden" inert={abierta ? undefined : true} aria-hidden={abierta ? undefined : 'true'}>
          <div role="radiogroup" className="border-t border-[#222222]">
            {cat.examenes.map((est, i) => (
              <FilaDeExamen key={est.clave} t={t} idioma={idioma} catalogo={catalogo} est={est}
                marcado={elegido === est.clave} alElegir={alElegir} primera={i === 0} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// -- La hoja / ventana ---------------------------------------------------------------------------------

export default function EstiloDeExamen({ t, idioma, catalogo, inicial, alListo, alCerrar, volverA, testid = 'estilo-hoja' }) {
  const [elegido, setElegido] = useState(() => (inicial && estiloPorClave(catalogo, inicial) ? inicial : QUIZESSA));
  const categorias = examenesPorCategoria(catalogo);
  const [abierta, setAbierta] = useState(() => {
    const est = estiloPorClave(catalogo, inicial);
    return est && est.clave !== QUIZESSA ? est.categoria : null;
  });
  const [busqueda, setBusqueda] = useState('');
  const cerrarRef = useRef(alCerrar);
  cerrarRef.current = alCerrar;
  const volverRef = useRef(volverA);
  const hoja = useHojaDeLaCasa(alCerrar);
  useEffect(() => {
    const tecla = (e) => { if (e.key === 'Escape') cerrarRef.current(); };
    document.addEventListener('keydown', tecla);
    const volver = volverRef.current;
    return () => {
      document.removeEventListener('keydown', tecla);
      const b = volver && volver.current;
      if (b && typeof b.focus === 'function') b.focus();
    };
  }, []);

  const buscando = busqueda.trim().length > 0;
  const resultados = buscando ? examenesQueCoinciden(catalogo, busqueda) : [];
  const alternar = (clave) => setAbierta((actual) => (actual === clave ? null : clave));
  const etiquetaId = `${testid}-titulo`;

  return createPortal(
    <div ref={hoja.veloRef} className={`${FONDO_DEL_ESTILO} ${ENTRA_EL_VELO}`}
      onClick={(e) => { e.stopPropagation(); if (e.target === e.currentTarget) alCerrar(); }}
      data-testid={`${testid}-fondo`}>
      <div ref={hoja.cajaRef} role="dialog" aria-modal="true" aria-labelledby={etiquetaId}
        className={`${CAJA_DEL_ESTILO} ${ENTRA_LA_CAJA}`} onClick={(e) => e.stopPropagation()} data-testid={testid}>
        <FilaDeAgarradera agarradera={hoja.agarradera} testid={`${testid}-agarradera`} />
        <button type="button" onClick={alCerrar} aria-label={t('common:a11y.close')}
          className="hidden md:flex absolute z-[2] top-4 right-4 w-10 h-10 rounded-xl items-center justify-center text-[#9A9A9A] hoverable:text-white hoverable:bg-[#1B1B1B] focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
          data-testid={`${testid}-cerrar`}>
          <X className="w-5 h-5" aria-hidden="true" />
        </button>
        <div className="flex-1 min-h-0 overflow-y-auto px-4 md:px-6 pt-3 md:pt-6 pb-4 max-md:overscroll-contain max-md:touch-pan-y" data-ventana-cuerpo="">
          <h2 id={etiquetaId} className="text-[20px] leading-7 font-bold text-[#F4F4F3] break-words md:pr-10">{t('dashboard:estilo.titulo')}</h2>
          <p className="mt-1.5 text-[14px] leading-5 text-[#9A9A9A] break-words">{t('dashboard:estilo.renglon')}</p>

          <div className="relative mt-4">
            <Search className="w-4 h-4 text-[#9A9A9A] absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" aria-hidden="true" />
            <input type="text" value={busqueda} onChange={(e) => setBusqueda(e.target.value)}
              placeholder={t('dashboard:estilo.buscar')} aria-label={t('dashboard:estilo.buscar')}
              autoComplete="off" autoCorrect="off" spellCheck={false} enterKeyHint="search"
              className={CAMPO_DE_BUSQUEDA} data-testid={`${testid}-buscar`} />
            {busqueda ? (
              <button type="button" onClick={() => setBusqueda('')} aria-label={t('common:a11y.close')}
                className="absolute right-1 top-1/2 -translate-y-1/2 w-11 h-11 rounded-xl flex items-center justify-center text-[#9A9A9A] hoverable:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
                data-testid={`${testid}-limpiar`}>
                <X className="w-4 h-4" aria-hidden="true" />
              </button>
            ) : null}
          </div>

          <button type="button" role="radio" aria-checked={elegido === QUIZESSA} onClick={() => setElegido(QUIZESSA)}
            className="mt-4 w-full h-[72px] flex items-center gap-3 px-4 rounded-[18px] border-[1.5px] border-[#FF6B00] bg-[#FF6B00]/[0.08] text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
            data-testid={`${testid}-quizessa`}>
            <span className="flex-1 min-w-0">
              <span className="block text-[16px] leading-[22px] font-bold text-[#F4F4F3] truncate">{nombreLargo(catalogo, QUIZESSA, idioma) || t('dashboard:estilo.quizessa')}</span>
              <span className="text-[13px] leading-4 text-[#9A9A9A] line-clamp-2 break-words">{t('dashboard:estilo.default_renglon')}</span>
            </span>
            <Circulo marcado={elegido === QUIZESSA} />
          </button>

          {buscando ? (
            resultados.length > 0 ? (
              <div role="radiogroup" className="mt-4 rounded-[18px] border border-[#222222] bg-[#141414] overflow-hidden" data-testid={`${testid}-resultados`}>
                {resultados.map((est, i) => (
                  <FilaDeExamen key={est.clave} t={t} idioma={idioma} catalogo={catalogo} est={est}
                    marcado={elegido === est.clave} alElegir={setElegido} primera={i === 0} />
                ))}
              </div>
            ) : (
              <p className="mt-6 mb-2 text-center text-[14px] leading-5 text-[#9A9A9A]" data-testid={`${testid}-sin-resultados`}>{t('dashboard:estilo.sin_resultados')}</p>
            )
          ) : (
            <>
              <div className="mt-5 mb-3 flex items-center gap-3" aria-hidden="true">
                <span className="h-px flex-1 bg-[#2C2C2C]" />
                <span className="text-[12px] leading-4 uppercase tracking-[0.08em] text-[#9A9A9A] whitespace-nowrap">{t('dashboard:estilo.rotulo')}</span>
                <span className="h-px flex-1 bg-[#2C2C2C]" />
              </div>
              <div className="flex flex-col gap-2" data-testid={`${testid}-categorias`}>
                {categorias.map((cat) => (
                  <TarjetaDeCategoria key={cat.clave} t={t} idioma={idioma} catalogo={catalogo} cat={cat}
                    abierta={abierta === cat.clave} alAlternar={alternar} elegido={elegido} alElegir={setElegido} />
                ))}
              </div>
            </>
          )}
        </div>
        <div className="shrink-0 px-4 md:px-6 pt-3 pb-[max(1rem,env(safe-area-inset-bottom))] md:pb-6 flex md:justify-end border-t border-[#222222]">
          <button type="button" onClick={() => alListo(elegido)} className={BOTON_VENTANA_PRINCIPAL} data-testid={`${testid}-listo`}>
            {t('dashboard:estilo.listo')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

// -- La pastilla conectada a la sesion -----------------------------------------------------------------

/** La pastilla de la sesion; tocarla abre la hoja, "Listo" la guarda y tocar fuera no cambia nada. Sin perilla, nada. */
export function SelectorDeEstilo({ t, idioma, testid = 'estilo-pastilla' }) {
  const estilos = useEstilos();
  const [abierta, setAbierta] = useState(false);
  const botonRef = useRef(null);
  if (!estilos.on) return null;
  return (
    <>
      <PastillaDeEstilo t={t} idioma={idioma} catalogo={estilos} valor={estilos.preferido} botonRef={botonRef}
        alTocar={(e) => { if (e) e.stopPropagation(); setAbierta(true); }} testid={testid} />
      {abierta ? (
        <EstiloDeExamen t={t} idioma={idioma} catalogo={estilos} inicial={estilos.preferido} volverA={botonRef}
          alCerrar={() => setAbierta(false)}
          alListo={(clave) => { guardarPreferido(clave); setAbierta(false); }} />
      ) : null}
    </>
  );
}
